import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import MaskedInput from '../common/MaskedInput';
import { signup } from '../api/auth';
import { useTranslation } from '../context/TranslationContext';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import styles from './auth.module.css';

export default function Signup() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch') || 'Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await signup(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.row}>
        <aside className={styles.brandPanel}>
          <div className={styles.brandInner}>
            <div className={styles.brandHead}>
              <img src="/logo.svg" alt={t('appName')} className={styles.brandLogo} />
              <div>
                <div className={styles.brandName}>{t('appName')}</div>
                <div className={styles.brandTagline}>{t('appTagline')}</div>
              </div>
            </div>

            <ul className={styles.featureList}>
              <li className={styles.featureItem}>
                <span className={styles.featureCheck}>
                  <Icon name="fa-check" size="sm" strokeWidth={2.5} />
                </span>
                {t('loginFeatureReceipts')}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.featureCheck}>
                  <Icon name="fa-check" size="sm" strokeWidth={2.5} />
                </span>
                {t('loginFeatureBudget')}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.featureCheck}>
                  <Icon name="fa-check" size="sm" strokeWidth={2.5} />
                </span>
                {t('loginFeatureInsights')}
              </li>
            </ul>
          </div>
        </aside>

        <main className={styles.formPanel}>
          <div className={styles.formCard}>
            <h1 className={styles.title}>{t('signup')}</h1>

            {error && (
              <div className={styles.error} role="alert">
                <Icon name="fa-info-circle" size="sm" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="username" className={styles.label}>{t('email')}</label>
                <input
                  type="text"
                  className={styles.input}
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="password" className={styles.label}>{t('password')}</label>
                <MaskedInput
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="confirmPassword" className={styles.label}>
                  {t('confirmPassword')}
                </label>
                <MaskedInput
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                disabled={isLoading}
                className={styles.submit}
              >
                {t('signupButton')}
              </Button>
            </form>

            <p className={styles.altLink}>
              {t('alreadyHaveAccount') || 'Already have an account?'}{' '}
              <Link to="/login">{t('login')}</Link>
            </p>
            <p className={styles.legalLink}>
              <Link to="/privacy">{t('privacy.link')}</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
